import type { RefObject } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { createPortal } from 'react-dom';

export type PromptAssetItem = {
  id: string;
  title: string;
  description?: string | null;
  prompt: string;
  public?: boolean;
};

type SlashRange = { start: number; end: number };

export type UsePromptSummonArgs = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  enabled: boolean;
  isComposing: boolean;
  t: (key: string) => string;
  onApplyValue: (nextValue: string, caretPos: number) => void;
  perPage?: number;
};

function parseSlashToken(text: string, cursor: number | null | undefined) {
  try {
    const c = typeof cursor === 'number' ? cursor : undefined;
    if (c == null) return null;
    const beforeCursor = text.slice(0, c);
    const slashIdx = beforeCursor.lastIndexOf('/');
    if (slashIdx < 0) return null;
    if (slashIdx > 0 && !/\s/.test(beforeCursor[slashIdx - 1] || '')) return null;
    const token = beforeCursor.slice(slashIdx + 1);
    if (token.length === 0) return { start: slashIdx, end: c, query: '' };
    if (/\s/.test(token)) return null;
    return { start: slashIdx, end: c, query: token };
  } catch {
    return null;
  }
}

export function usePromptSummon({
  textareaRef,
  enabled,
  isComposing,
  t,
  onApplyValue,
  perPage = 20,
}: UsePromptSummonArgs) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [range, setRange] = useState<SlashRange | null>(null);
  const [loading, setLoading] = useState(false);
  const [myItems, setMyItems] = useState<PromptAssetItem[]>([]);
  const [publicItems, setPublicItems] = useState<PromptAssetItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number; width: number; listMaxH: number } | null>(null);

  const close = () => {
    setOpen(false);
    setQuery('');
    setDebouncedQuery('');
    setRange(null);
    setMenuPos(null);
    setLoading(false);
    setMyItems([]);
    setPublicItems([]);
    setActiveIndex(0);
  };

  const updateMenuPos = () => {
    const el = textareaRef.current;
    if (!el) return;
    try {
      const rect = el.getBoundingClientRect();
      const viewportW = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
      const margin = 8;
      const headerH = 36;
      const listMaxHDefault = 260;

      // rect.width is in viewport pixels (accounts for canvas zoom); clamp it
      // so the menu stays usable at extreme zoom levels.
      const width = Math.max(300, Math.min(rect.width, 600));

      const centerX = rect.left + rect.width / 2;
      let left = centerX - width / 2;
      if (left + width > viewportW - margin) left = Math.max(margin, viewportW - margin - width);
      left = Math.max(margin, left);

      // Strongly prefer below the textarea so the menu stays near the input.
      // The toolbar sits below the node, so flipping above places the menu
      // far from the textarea (above the entire node).
      const downTop = rect.bottom + margin;
      const spaceBelow = Math.max(0, viewportH - margin - downTop - headerH);

      if (spaceBelow >= 80) {
        setMenuPos({ left, top: downTop, width, listMaxH: Math.min(listMaxHDefault, spaceBelow) });
      } else {
        const spaceAbove = Math.max(0, rect.top - margin * 2 - headerH);
        if (spaceAbove >= 80) {
          const usableListH = Math.min(listMaxHDefault, spaceAbove);
          const top = rect.top - margin - usableListH - headerH;
          setMenuPos({ left, top: Math.max(margin, top), width, listMaxH: usableListH });
        } else {
          setMenuPos({ left, top: downTop, width, listMaxH: Math.max(60, spaceBelow) });
        }
      }
    } catch {}
  };

  const syncFromTextarea = (el: HTMLTextAreaElement | null) => {
    if (!enabled) {
      close();
      return;
    }
    if (!el) return;
    if (isComposing) {
      close();
      return;
    }
    try {
      const parsed = parseSlashToken(el.value, el.selectionStart);
      if (!parsed) {
        close();
        return;
      }
      setOpen(true);
      setQuery(parsed.query);
      setRange({ start: parsed.start, end: parsed.end });
      updateMenuPos();
    } catch {}
  };

  // Debounce query to reduce request pressure.
  useEffect(() => {
    if (!enabled || !open) {
      setDebouncedQuery('');
      return;
    }
    const raw = String(query || '');
    if (!raw.trim()) {
      setDebouncedQuery('');
      return;
    }
    const tmr = window.setTimeout(() => {
      setDebouncedQuery(raw);
    }, 200);
    return () => {
      try { window.clearTimeout(tmr); } catch {}
    };
  }, [enabled, open, query]);

  // Fetch prompt assets (my + public) when open.
  useEffect(() => {
    if (!enabled || !open) return;
    const ac = new AbortController();
    const q = String(debouncedQuery || '').trim();
    if (!q) {
      setLoading(false);
      setMyItems([]);
      setPublicItems([]);
      return () => {
        try { ac.abort(); } catch {}
      };
    }

    setLoading(true);

    const fetchScope = async (scope: 'my' | 'public') => {
      const params = new URLSearchParams();
      params.set('scope', scope);
      if (q) params.set('q', q);
      params.set('page', '1');
      params.set('perPage', String(perPage));
      const res = await fetch(`/api/prompt-assets?${params.toString()}`, { signal: ac.signal });
      const data = await res.json();
      if (!res.ok || data?.error) throw new Error(data?.error || 'FAILED');
      const items = Array.isArray(data?.items) ? data.items : [];
      return items as PromptAssetItem[];
    };

    Promise.allSettled([fetchScope('my'), fetchScope('public')])
      .then((results) => {
        if (ac.signal.aborted) return;
        const my = results[0].status === 'fulfilled' ? results[0].value : [];
        const pub = results[1].status === 'fulfilled' ? results[1].value : [];
        setMyItems(my);
        setPublicItems(pub);
      })
      .catch(() => {})
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => {
      try { ac.abort(); } catch {}
    };
  }, [enabled, open, debouncedQuery, perPage]);

  const flatCount = myItems.length + publicItems.length;
  const hasSearchQuery = useMemo(() => String(debouncedQuery || '').trim().length > 0, [debouncedQuery]);
  useEffect(() => {
    if (!enabled || !open) return;
    setActiveIndex(0);
  }, [enabled, open, debouncedQuery, myItems.length, publicItems.length]);

  // Keep position updated while open.
  useEffect(() => {
    if (!enabled || !open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    const onResize = () => updateMenuPos();
    const onScroll = () => updateMenuPos();
    const onWheel = () => requestAnimationFrame(() => updateMenuPos());
    try { window.addEventListener('resize', onResize); } catch {}
    try { window.addEventListener('scroll', onScroll, true); } catch {}
    try { window.addEventListener('wheel', onWheel, { passive: true, capture: true }); } catch {}
    return () => {
      try { window.removeEventListener('resize', onResize); } catch {}
      try { window.removeEventListener('scroll', onScroll, true); } catch {}
      try { window.removeEventListener('wheel', onWheel, true); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, open]);

  const insertAtSlash = (item: PromptAssetItem) => {
    const el = textareaRef.current;
    if (!el || !range) return;
    try {
      const text = String(el.value ?? '');
      const before = text.slice(0, range.start);
      const after = text.slice(range.end);
      const rawInsert = String(item?.prompt ?? '');
      const insert = rawInsert.endsWith(' ') || after.startsWith(' ') || after.startsWith('\n') ? rawInsert : `${rawInsert} `;
      const next = `${before}${insert}${after}`;
      const caret = before.length + insert.length;
      onApplyValue(next, caret);
      close();
    } catch {}
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (!enabled || !open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (!hasSearchQuery) return;
    if (loading) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setActiveIndex((prev) => Math.min(Math.max(0, flatCount - 1), prev + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setActiveIndex((prev) => Math.max(0, prev - 1));
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      const picked =
        activeIndex < myItems.length ? myItems[activeIndex] : publicItems[Math.max(0, activeIndex - myItems.length)];
      if (!picked) return;
      e.preventDefault();
      e.stopPropagation();
      insertAtSlash(picked);
    }
  };

  const portal = useMemo(() => {
    if (!enabled || !open || !menuPos) return null;
    if (typeof document === 'undefined') return null;
    return createPortal(
      <div
        className={cn(
          "nodrag nowheel fixed z-[9999] overflow-hidden rounded-xl border bg-popover shadow-2xl",
          "backdrop-blur-sm"
        )}
        style={{
          left: `${menuPos.left}px`,
          top: `${menuPos.top}px`,
          width: `${menuPos.width}px`,
        }}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        <div className="px-3 py-2 text-[11px] text-muted-foreground border-b">
          {t('输入 "/" 搜索提示词库')}
        </div>
        <div className="overflow-y-auto custom-scrollbar" style={{ maxHeight: `${menuPos.listMaxH}px` }}>
          {loading ? (
            <div className="p-3 text-sm text-muted-foreground">{t('Loading…')}</div>
          ) : (
            <>
              {!hasSearchQuery ? (
                <div className="p-3 text-sm text-muted-foreground">
                  {t('Keep typing keywords to search prompts…')}
                </div>
              ) : null}
              {myItems.length > 0 ? (
                <div className="p-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('My prompts')}</div>
                  {myItems.map((it, idx) => {
                    const active = idx === activeIndex;
                    return (
                      <button
                        key={`my-${it.id}`}
                        type="button"
                        className={cn(
                          "w-full text-left rounded-md px-2 py-2 transition-colors",
                          active ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                        )}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => insertAtSlash(it)}
                      >
                        <div className="truncate text-sm font-medium">{it.title}</div>
                        {it.description ? <div className="truncate text-xs opacity-80">{it.description}</div> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {myItems.length > 0 && publicItems.length > 0 ? <div className="h-px bg-border my-1" /> : null}

              {publicItems.length > 0 ? (
                <div className="p-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{t('Public prompts')}</div>
                  {publicItems.map((it, idx) => {
                    const flatIdx = myItems.length + idx;
                    const active = flatIdx === activeIndex;
                    return (
                      <button
                        key={`pub-${it.id}`}
                        type="button"
                        className={cn(
                          "w-full text-left rounded-md px-2 py-2 transition-colors",
                          active ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                        )}
                        onMouseEnter={() => setActiveIndex(flatIdx)}
                        onClick={() => insertAtSlash(it)}
                      >
                        <div className="truncate text-sm font-medium">{it.title}</div>
                        {it.description ? <div className="truncate text-xs opacity-80">{it.description}</div> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {hasSearchQuery && !myItems.length && !publicItems.length ? (
                <div className="p-3 text-sm text-muted-foreground">{t('No matching prompts')}</div>
              ) : null}
            </>
          )}
        </div>
      </div>,
      document.body
    );
  }, [enabled, open, menuPos, t, loading, myItems, publicItems, activeIndex, hasSearchQuery]);

  return {
    open,
    loading,
    close,
    syncFromTextarea,
    onKeyDown,
    portal,
  };
}
